import { ChangeEvent, DragEvent, FormEvent, useEffect, useMemo, useState } from "react";
import { ExternalLink, FolderOpen, RefreshCw, Trash2 } from "lucide-react";
import { azureSqlService } from "@/services/azureSqlService.js";
import { ifcAdminService, type IfcAdminFileRecord, type IfcUploadResult } from "@/services/ifcAdminService";
import RecentAccessLogPane from "@/components/accessLogs/RecentAccessLogPane";
import type { AdminAccessLogRecord } from "@/types/accessLogs";
import { Button, Card, CardHeader, EmptyState, StatusBadge } from "@/ui/components";

type ProjectOption = {
  projectId: number;
  projectNumber: string;
  projectName: string;
  siteAddress: string;
  suburb: string;
  postCode: string;
};

type UploadStage = "idle" | "searching" | "loading" | "preparing" | "uploading" | "registered" | "openingViewer";

function normalizeProjectRows(response: unknown): ProjectOption[] {
  const rows = Array.isArray(response)
    ? response
    : response && typeof response === "object" && Array.isArray((response as any).data)
      ? (response as any).data
      : [];

  return rows.flatMap((project: any) => {
    const projectId = Number(project?.ProjectId ?? project?.Id);
    if (!Number.isFinite(projectId) || projectId <= 0) return [];
    return [{
      projectId,
      projectNumber: String(project?.ProjectCode || project?.ProjectNumber || "").trim(),
      projectName: String(project?.ProjectName || project?.Title || "Unnamed project").trim(),
      siteAddress: String(project?.SiteAddress || project?.Address || "").trim(),
      suburb: String(project?.Suburb || "").trim(),
      postCode: String(project?.PostCode ?? project?.Postcode ?? "").trim(),
    }];
  });
}

function normalizeComparable(value: unknown) {
  return String(value || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
}

export function extractProjectNumberFromIfcFileName(fileName: string): string {
  const baseName = String(fileName || "").replace(/\.[^.]+$/, "");
  const patterns = [
    /^\s*(\d{4,6})(?=\s*[-_ ]|\b)/,
    /\b(project|job|quote|quotation)\s*[-_# ]\s*(\d{4,6})\b/i,
    /\b(\d{4,6})\b/,
  ];

  for (const pattern of patterns) {
    const match = baseName.match(pattern);
    const candidate = match?.[2] || match?.[1];
    if (candidate) return candidate;
  }
  return "";
}

export function extractProjectNumberFromProjectMatchInput(value: string): string {
  const match = String(value || "").match(/\b\d{4,6}\b/);
  return match?.[0] || "";
}

function stripProjectPrefix(fileName: string, projectNumber: string) {
  const escapedProjectNumber = projectNumber.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return fileName.replace(new RegExp(`^\\s*${escapedProjectNumber}\\s*[-_ ]\\s*`, "i"), "").trim();
}

function buildPreviewRegisteredName(project: ProjectOption | null, file: File | null) {
  if (!file) return "Choose an IFC file";
  const projectNumber = project?.projectNumber || extractProjectNumberFromIfcFileName(file.name);
  if (!projectNumber) return file.name;
  return `${projectNumber} - ${stripProjectPrefix(file.name, projectNumber)}`;
}

function formatFileSize(bytes?: number | null) {
  const value = Number(bytes || 0);
  if (!Number.isFinite(value) || value <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let next = value;
  let unitIndex = 0;
  while (next >= 1024 && unitIndex < units.length - 1) {
    next /= 1024;
    unitIndex += 1;
  }
  return `${next.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function formatDate(value?: string | null) {
  if (!value) return "No date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
}

function formatVersion(file: IfcAdminFileRecord) {
  return file.versionLabel || formatDate(file.versionCreatedAt || file.createdAt);
}

function projectTitle(project: ProjectOption) {
  return project.projectNumber || `Project ${project.projectId}`;
}

function projectAddress(project: ProjectOption) {
  return [project.siteAddress, project.suburb, project.postCode].filter(Boolean).join(", ") || "No saved address";
}

function shouldShowInventoryProjectName(group: IfcFileGroup): boolean {
  const projectName = normalizeComparable(group.projectName);
  const siteAddress = normalizeComparable(group.siteAddress);
  if (!projectName || projectName === normalizeComparable("Unnamed project")) return false;
  if (!siteAddress || siteAddress === normalizeComparable("No saved address")) return true;
  return projectName !== siteAddress && !projectName.includes(siteAddress) && !siteAddress.includes(projectName);
}

function getUploadStageLabel(stage: UploadStage) {
  switch (stage) {
    case "searching":
      return "Searching project";
    case "loading":
      return "Loading files";
    case "preparing":
      return "Preparing upload";
    case "uploading":
      return "Uploading IFC";
    case "registered":
      return "Registered";
    case "openingViewer":
      return "Opening viewer";
    default:
      return "Ready";
  }
}

type IfcFileGroup = {
  key: string;
  projectLabel: string;
  projectName: string;
  siteAddress: string;
  files: IfcAdminFileRecord[];
};

function getIfcFileProjectLabel(file: IfcAdminFileRecord) {
  return file.projectNumber || (file.projectId ? `Project ${file.projectId}` : "Unmatched project");
}

function groupIfcFilesByProject(files: IfcAdminFileRecord[]): IfcFileGroup[] {
  const groups = new Map<string, IfcFileGroup>();
  files.forEach((file) => {
    const key = String(file.projectId ?? file.projectNumber ?? "unmatched");
    const existing = groups.get(key);
    if (existing) {
      existing.files.push(file);
      return;
    }
    groups.set(key, {
      key,
      projectLabel: getIfcFileProjectLabel(file),
      projectName: file.projectName || "Unnamed project",
      siteAddress: file.siteAddress || "No saved address",
      files: [file],
    });
  });
  return Array.from(groups.values()).map((group) => ({
    ...group,
    files: [...group.files].sort((a, b) => {
      const aTime = new Date(a.versionCreatedAt || a.createdAt).getTime();
      const bTime = new Date(b.versionCreatedAt || b.createdAt).getTime();
      return (Number.isFinite(bTime) ? bTime : 0) - (Number.isFinite(aTime) ? aTime : 0);
    }),
  }));
}

export default function IfcAdminUploadPanel() {
  const [projectQuery, setProjectQuery] = useState("");
  const [projectOptions, setProjectOptions] = useState<ProjectOption[]>([]);
  const [selectedProject, setSelectedProject] = useState<ProjectOption | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [guessedProjectNumber, setGuessedProjectNumber] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [stage, setStage] = useState<UploadStage>("idle");
  const [feedback, setFeedback] = useState<string | null>(null);
  const [feedbackTone, setFeedbackTone] = useState<"success" | "error">("success");
  const [lastUpload, setLastUpload] = useState<IfcUploadResult | null>(null);
  const [registeredFiles, setRegisteredFiles] = useState<IfcAdminFileRecord[]>([]);
  const [openingFileId, setOpeningFileId] = useState<string | null>(null);
  const [accessLogs, setAccessLogs] = useState<AdminAccessLogRecord[]>([]);
  const [accessLogsLoading, setAccessLogsLoading] = useState(false);
  const [accessLogsError, setAccessLogsError] = useState<string | null>(null);

  const uploading = stage === "preparing" || stage === "uploading";
  const openingViewer = stage === "openingViewer";
  const searching = stage === "searching";
  const loadingFiles = stage === "loading";
  const busy = uploading || searching || loadingFiles || openingViewer;
  const canUpload = useMemo(() => Boolean(selectedProject && selectedFile && !busy), [busy, selectedProject, selectedFile]);
  const debugProjectNumber = selectedProject?.projectNumber || extractProjectNumberFromProjectMatchInput(projectQuery);
  const canOpenDebugViewer = Boolean(debugProjectNumber && !busy);
  const previewFileName = buildPreviewRegisteredName(selectedProject, selectedFile);
  const fileGroups = useMemo(() => groupIfcFilesByProject(registeredFiles), [registeredFiles]);

  async function loadRegisteredFiles(projectId = selectedProject?.projectId || null, options: { quiet?: boolean } = {}) {
    if (!options.quiet) {
      setStage((current) => current === "preparing" || current === "uploading" || current === "openingViewer" ? current : "loading");
    }
    try {
      const files = await ifcAdminService.listFiles(projectId);
      setRegisteredFiles(files);
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : "IFC files could not be loaded.");
      setFeedbackTone("error");
    } finally {
      if (!options.quiet) {
        setStage((current) => current === "loading" ? "idle" : current);
      }
    }
  }

  async function loadAccessLogs() {
    setAccessLogsLoading(true);
    setAccessLogsError(null);
    try {
      setAccessLogs(await ifcAdminService.listAccessLogs(30));
    } catch (error) {
      setAccessLogsError(error instanceof Error ? error.message : "IFC access logs could not be loaded.");
    } finally {
      setAccessLogsLoading(false);
    }
  }

  useEffect(() => {
    void loadRegisteredFiles(null);
    void loadAccessLogs();
  }, []);

  async function searchProjectsByQuery(query: string, options: { autoSelectExact?: boolean } = {}) {
    const trimmedQuery = query.trim();
    if (trimmedQuery.length < 2) {
      setFeedback("Enter at least 2 characters to search projects.");
      setFeedbackTone("error");
      return;
    }

    setStage("searching");
    setFeedback(null);
    setProjectOptions([]);
    try {
      const results = await azureSqlService.searchProjects(trimmedQuery, 8, { lifecycle: "all" });
      const normalized = normalizeProjectRows(results);
      setProjectOptions(normalized);

      if (options.autoSelectExact) {
        const exactMatches = normalized.filter((project) => normalizeComparable(project.projectNumber) === normalizeComparable(trimmedQuery));
        if (exactMatches.length === 1) {
          selectProject(exactMatches[0], { quiet: true });
          setFeedback(`Matched project ${projectTitle(exactMatches[0])} from the IFC file name.`);
          setFeedbackTone("success");
          return;
        }
      }

      setFeedback(normalized.length ? `${normalized.length} matching project row(s) found. Confirm the correct one before upload.` : "No project rows matched that search.");
      setFeedbackTone(normalized.length ? "success" : "error");
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : "Project search failed.");
      setFeedbackTone("error");
    } finally {
      setStage("idle");
    }
  }

  async function searchProjects(event?: FormEvent) {
    event?.preventDefault();
    await searchProjectsByQuery(projectQuery);
  }

  function selectProject(project: ProjectOption, options: { quiet?: boolean } = {}) {
    setSelectedProject(project);
    setProjectQuery([project.projectNumber, project.projectName].filter(Boolean).join(" - "));
    void loadRegisteredFiles(project.projectId);
    if (!options.quiet) {
      setFeedback(`Selected ${projectTitle(project)}.`);
      setFeedbackTone("success");
    }
  }

  function applyFile(file: File | null) {
    setLastUpload(null);
    setSelectedProject(null);
    setStage("idle");
    if (!file) {
      setSelectedFile(null);
      setGuessedProjectNumber("");
      return;
    }
    if (!/\.ifc$/i.test(file.name)) {
      setSelectedFile(null);
      setGuessedProjectNumber("");
      setFeedback("Choose an .ifc file.");
      setFeedbackTone("error");
      return;
    }

    const guessed = extractProjectNumberFromIfcFileName(file.name);
    setSelectedFile(file);
    setGuessedProjectNumber(guessed);
    setProjectQuery(guessed || "");

    if (guessed) {
      setFeedback(`Detected project number ${guessed} from ${file.name}.`);
      setFeedbackTone("success");
      void searchProjectsByQuery(guessed, { autoSelectExact: true });
      return;
    }

    setFeedback(`${file.name} selected. Search for the project row to continue.`);
    setFeedbackTone("success");
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    applyFile(event.target.files?.[0] || null);
  }

  function handleDrop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    setDragActive(false);
    applyFile(event.dataTransfer.files?.[0] || null);
  }

  async function uploadIfc() {
    if (!selectedProject || !selectedFile) {
      setFeedback("Confirm a project row and choose an IFC file first.");
      setFeedbackTone("error");
      return;
    }

    setStage("preparing");
    setFeedback("Creating a private upload URL...");
    setFeedbackTone("success");
    try {
      setStage("uploading");
      const result = await ifcAdminService.uploadIfcForProject(selectedProject.projectId, selectedFile);
      setLastUpload(result);
      setFeedback(`Uploaded and registered ${result.fileName}.`);
      setFeedbackTone("success");
      setSelectedFile(null);
      setGuessedProjectNumber("");
      setStage("registered");
      await loadRegisteredFiles(selectedProject.projectId, { quiet: true });
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : "IFC upload failed.");
      setFeedbackTone("error");
      setStage("idle");
    }
  }

  async function openDebugViewer(file?: IfcAdminFileRecord) {
    const projectNumber = file?.projectNumber || selectedProject?.projectNumber || extractProjectNumberFromProjectMatchInput(projectQuery);
    if (!file && !projectNumber) {
      setFeedback("Enter or select a project number before opening the debug viewer.");
      setFeedbackTone("error");
      return;
    }

    setOpeningFileId(file?.id || null);
    setStage("openingViewer");
    setFeedback(file
      ? `Creating debug viewer session for ${file.fileName}...`
      : `Creating debug viewer session for project ${projectNumber}...`);
    setFeedbackTone("success");
    try {
      const result = await ifcAdminService.openDebugViewer(projectNumber || "", file?.id);
      window.open(result.viewerPath, "_blank", "noopener,noreferrer");
      setFeedback(`Opened debug viewer for ${result.fileName || file?.fileName || `project ${projectNumber}`}.`);
      setFeedbackTone("success");
      void loadAccessLogs();
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : "Debug viewer could not be opened.");
      setFeedbackTone("error");
    } finally {
      setOpeningFileId(null);
      setStage("idle");
    }
  }

  async function deleteIfcFile(file: IfcAdminFileRecord) {
    const confirmed = window.confirm(`Delete ${file.fileName}? This removes it from public IFC viewer lookup.`);
    if (!confirmed) return;

    try {
      await ifcAdminService.deleteFile(file.id);
      setFeedback(`Deleted ${file.fileName}.`);
      setFeedbackTone("success");
      await loadRegisteredFiles(selectedProject?.projectId || null, { quiet: true });
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : "IFC file could not be deleted.");
      setFeedbackTone("error");
    }
  }

  return (
    <Card
      header={(
        <CardHeader
          title="IFC Model Upload"
          subtitle="Drop an exported IFC file, confirm the matched project row, then register it for the public OTP viewer."
          actions={<StatusBadge status={stage}>{getUploadStageLabel(stage)}</StatusBadge>}
        />
      )}
    >
      <div className="ifc-admin-workspace">
        <aside className="ifc-admin-inventory" aria-label="Registered IFC models">
          <div className="ifc-admin-inventory__header">
            <div>
              <h3>Model inventory</h3>
              <p>{selectedProject ? `${projectTitle(selectedProject)} IFC revisions` : "Latest active IFC models by project"}</p>
            </div>
            <StatusBadge status={String(registeredFiles.length)}>{registeredFiles.length} files</StatusBadge>
          </div>

          <div className="ifc-admin-inventory__actions">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              iconBefore={<RefreshCw size={15} aria-hidden="true" />}
              onClick={() => loadRegisteredFiles(selectedProject?.projectId || null)}
              disabled={busy}
            >
              Refresh
            </Button>
          </div>

          {fileGroups.length ? (
            <div className="ifc-admin-inventory__list">
              {fileGroups.map((group) => (
                <section key={group.key} className="ifc-admin-inventory__group">
                  <div className="ifc-admin-inventory__project">
                    <div className="ifc-admin-inventory__project-row">
                      <span>{group.projectLabel}</span>
                      <span className="ifc-admin-inventory__project-separator" aria-hidden="true">·</span>
                      <strong>{group.siteAddress}</strong>
                    </div>
                    {shouldShowInventoryProjectName(group) ? <small>{group.projectName}</small> : null}
                  </div>
                  <div className="ifc-admin-inventory__files">
                    {group.files.map((file, index) => (
                      <article key={file.id} className="ifc-admin-inventory-file">
                        <div className="ifc-admin-inventory-file__main">
                          <strong>{file.fileName}</strong>
                          <div className="company-ai-source-strip">
                            {index === 0 ? <span className="company-ai-source-pill">Latest</span> : null}
                            <span className="company-ai-source-pill">Revision {formatDate(file.versionCreatedAt || file.createdAt)}</span>
                            <span className="company-ai-source-pill">{formatFileSize(file.fileSizeBytes)}</span>
                          </div>
                          <span>{formatVersion(file)}</span>
                          <small>{file.blobPath}</small>
                        </div>
                        <div className="ifc-admin-inventory-file__actions">
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            iconBefore={<ExternalLink size={15} aria-hidden="true" />}
                            onClick={() => openDebugViewer(file)}
                            disabled={busy}
                          >
                            {openingFileId === file.id ? "Opening..." : "Open"}
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            iconBefore={<Trash2 size={15} aria-hidden="true" />}
                            onClick={() => deleteIfcFile(file)}
                            disabled={busy}
                          >
                            Delete
                          </Button>
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={<FolderOpen size={28} />}
              title={loadingFiles ? "Loading IFC models" : "No IFC models registered"}
              description={selectedProject ? "This project has no active IFC revisions." : "Uploaded IFC models will appear here."}
            />
          )}
        </aside>

        <div className="ifc-admin-upload">
          <section className="ifc-admin-upload__hero" aria-label="IFC file upload">
            <label
              className={`ifc-admin-dropzone${dragActive ? " ifc-admin-dropzone--active" : ""}`}
              onDragOver={(event) => {
                event.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
            >
              <input type="file" accept=".ifc" onChange={handleFileChange} disabled={uploading} />
              <span className="ifc-admin-dropzone__title">{selectedFile ? selectedFile.name : "Choose or drop an IFC file"}</span>
              <span className="ifc-admin-dropzone__meta">
                {selectedFile ? `${formatFileSize(selectedFile.size)} · ${guessedProjectNumber ? `detected project ${guessedProjectNumber}` : "project number not detected"}` : "File name can start with the project number, e.g. 25092 - Site Name.ifc"}
              </span>
            </label>

            <div className="ifc-admin-upload__summary">
              <div>
                <span>Registered name</span>
                <strong>{previewFileName}</strong>
              </div>
              <div>
                <span>Blob path</span>
                <strong>{lastUpload?.blobPath || "ifc/{project number}/{AEST version}-{file}.ifc"}</strong>
              </div>
              <div>
                <span>Version</span>
                <strong>{lastUpload?.versionLabel || "Created from the upload date"}</strong>
              </div>
            </div>
          </section>

          <section className="ifc-admin-upload__section" aria-label="Project match">
            <div className="ifc-admin-upload__section-header">
              <div>
                <h3>Project match</h3>
                <p>Use the detected project number or search manually, then confirm one existing PMApp project row.</p>
              </div>
              {selectedProject ? <StatusBadge status="selected">{projectTitle(selectedProject)}</StatusBadge> : null}
            </div>

            <form className="ifc-admin-search" onSubmit={searchProjects}>
              <input
                aria-label="Search projects for IFC upload"
                value={projectQuery}
                onChange={(event) => setProjectQuery(event.target.value)}
                placeholder="Project number, name, suburb, or street address"
                disabled={searching || uploading}
              />
              <Button type="submit" disabled={searching || uploading}>{searching ? "Searching..." : "Search"}</Button>
            </form>

            {projectOptions.length ? (
              <div className="ifc-admin-project-grid">
                {projectOptions.map((project) => (
                  <button
                    key={project.projectId}
                    type="button"
                    className={`ifc-admin-project-card${selectedProject?.projectId === project.projectId ? " ifc-admin-project-card--selected" : ""}`}
                    onClick={() => selectProject(project)}
                    disabled={uploading}
                  >
                    <span>{projectTitle(project)}</span>
                    <strong>{project.projectName || "Unnamed project"}</strong>
                    <small>{projectAddress(project)}</small>
                  </button>
                ))}
              </div>
            ) : (
              <EmptyState
                icon={<FolderOpen size={28} />}
                title={selectedFile ? "Project match pending" : "Start with an IFC file"}
                description={selectedFile ? "Search results will appear here. Confirm the right project before upload." : "Choose an IFC file first so the project number can be detected from its name."}
              />
            )}
          </section>

          <section className="ifc-admin-upload__section ifc-admin-upload__section--confirm" aria-label="Upload confirmation">
            <div>
              <h3>Upload target</h3>
              <p>{selectedProject ? `${projectTitle(selectedProject)} · ${selectedProject.projectName || "Unnamed project"}` : "No project selected yet."}</p>
            </div>
            <div className="ifc-admin-upload__actions">
              <Button onClick={() => openDebugViewer()} disabled={!canOpenDebugViewer}>
                {openingViewer ? "Opening..." : "Debug Viewer"}
              </Button>
              <Button onClick={uploadIfc} disabled={!canUpload}>
                {uploading ? "Uploading..." : "Upload IFC"}
              </Button>
            </div>
          </section>

          {feedback ? (
            <div className={`company-ai-feedback company-ai-feedback--${feedbackTone === "error" ? "error" : "success"}`}>
              {feedback}
            </div>
          ) : null}

          {lastUpload ? (
            <div className="ifc-admin-upload__registered">
              <div>
                <span>Registered IFC file</span>
                <strong>{lastUpload.fileName}</strong>
              </div>
              <div className="company-ai-source-strip">
                <span className="company-ai-source-pill">Id: {lastUpload.id}</span>
                {lastUpload.versionLabel ? <span className="company-ai-source-pill">Version: {lastUpload.versionLabel}</span> : null}
                <span className="company-ai-source-pill">Blob: {lastUpload.blobPath}</span>
              </div>
            </div>
          ) : null}
        </div>

        <RecentAccessLogPane
          logs={accessLogs}
          loading={accessLogsLoading}
          error={accessLogsError}
          onRefresh={() => void loadAccessLogs()}
          tone="ifc"
          subtitle="Latest viewer and verification activity"
        />
      </div>
    </Card>
  );
}
